import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  ArrowLeft,
  ArrowRight,
  Copy,
  Flag,
  HelpCircle,
  Lightbulb,
  Lock,
  Pencil,
  Play,
  Plus,
  RotateCcw,
  UserX,
} from 'lucide-react';
import { Button } from '../../../ui/button';
import { Input } from '../../../ui/input';
import ConfirmDialog from '../../common/ConfirmDialog';
import EmptyState from '../../common/EmptyState';
import BracketView from '../../../Public/stages/BracketView';
import LeaderboardStageView from '../../../Public/stages/LeaderboardStageView';
import StageStepper, { StageStatusPill } from '../../../Public/stages/StageStepper';
import MappingVotingControl from '../../MappingVotingControl';
import StageEditorDialog from './StageEditorDialog';
import StartStageDialog from './StartStageDialog';
import CompleteStageDialog from './CompleteStageDialog';
import BracketResultDialog from './BracketResultDialog';
import LeaderboardResultsEditor from './LeaderboardResultsEditor';
import {
  deleteStage,
  moveStage,
  removeStageParticipants,
  reopenStage,
  resetStage,
  subscribeStages,
} from '../../../../database/tournamentStages';
import { subscribePlayers } from '../../../../database/tournamentPlayers';
import { subscribeVotes } from '../../../../database/tournamentVotes';
import {
  canWithdrawFrom,
  focusStageId,
  hasVoteCategory,
  nextStepHint,
  orphanedParticipants,
  stageActions,
  stageSummary,
  voteAverages,
} from '../../../../lib/stages';
import { handleFirebaseError } from '../../../../utils/errorHandler';

// Fake roster for the "before it starts" leaderboard preview (no participants yet).
const PLACEHOLDER_PLAYERS_BY_ID = {
  'seed:1': { name: 'Player 1' },
  'seed:2': { name: 'Player 2' },
  'seed:3': { name: 'Player 3' },
};
const PLACEHOLDER_PARTICIPANTS = ['seed:1', 'seed:2', 'seed:3'];

const StageManager = ({ tournamentId, tournament }) => {
  const [stages, setStages] = useState([]);
  const [players, setPlayers] = useState([]);
  const [playersLoaded, setPlayersLoaded] = useState(false);
  const [voteDocs, setVoteDocs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [editor, setEditor] = useState(null);
  const [startOpen, setStartOpen] = useState(false);
  const [completeOpen, setCompleteOpen] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [matchOpen, setMatchOpen] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setLoading(true);
    setError(null);
    const unsubStages = subscribeStages(
      tournamentId,
      (data) => {
        setStages(data);
        setLoading(false);
      },
      (err) => {
        setError(handleFirebaseError(err));
        setLoading(false);
      }
    );
    setPlayersLoaded(false);
    const unsubPlayers = subscribePlayers(
      tournamentId,
      (data) => {
        setPlayers(data);
        setPlayersLoaded(true);
      },
      (err) => toast.error('Could not load the roster.', { description: handleFirebaseError(err) })
    );
    return () => {
      unsubStages();
      unsubPlayers();
    };
  }, [tournamentId]);

  const hasVotes = stages.some(hasVoteCategory);
  useEffect(() => {
    if (!hasVotes) {
      setVoteDocs(null);
      return undefined;
    }
    const unsub = subscribeVotes(tournamentId, setVoteDocs, (err) =>
      toast.error('Could not load community votes.', { description: handleFirebaseError(err) })
    );
    return () => {
      unsub();
      setVoteDocs(null);
    };
  }, [tournamentId, hasVotes]);

  useEffect(() => {
    if (!stages.length) {
      setSelectedId(null);
      return;
    }
    if (!stages.some((s) => s.id === selectedId)) {
      setSelectedId(focusStageId(stages));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stages]);

  const playersById = useMemo(() => Object.fromEntries(players.map((p) => [p.id, p])), [players]);
  const index = stages.findIndex((s) => s.id === selectedId);
  const stage = index >= 0 ? stages[index] : null;
  const prev = index > 0 ? stages[index - 1] : null;
  const next = index >= 0 ? stages[index + 1] : null;
  const actions = stage ? stageActions(stages, index) : null;
  const votes =
    stage && hasVoteCategory(stage) && voteDocs ? voteAverages(voteDocs, stage.id) : null;
  const orphans = stage && playersLoaded ? orphanedParticipants(stage, playersById) : [];
  const orphansRemovable = stage && orphans.every((id) => canWithdrawFrom(stage, id));
  const hint = stage
    ? nextStepHint(stages, index, {
        rosterCount: players.length,
        votingOpen: tournament.votingOpen !== false,
        averageVisible: tournament.communityAverageVisible === true,
      })
    : null;
  const voterCount = (voteDocs || []).length;
  const overlayUrl = stage
    ? `${window.location.origin}/tournaments/${encodeURIComponent(tournament.slug || tournamentId)}/overlay?stage=${encodeURIComponent(stage.id)}`
    : '';

  const copyOverlayUrl = async () => {
    try {
      await navigator.clipboard.writeText(overlayUrl);
      toast.success('Overlay link copied.');
    } catch {
      toast.error('Could not copy the link. Select the URL and copy it manually.');
    }
  };

  const runWrite = async (fn, errorTitle, successMessage) => {
    setBusy(true);
    try {
      await fn();
      setConfirm(null);
      if (successMessage) toast.success(successMessage);
    } catch (err) {
      toast.error(errorTitle, { description: handleFirebaseError(err) });
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="h-20 animate-pulse rounded-md bg-muted motion-reduce:animate-none" />
        <div className="h-64 animate-pulse rounded-md bg-muted motion-reduce:animate-none" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/40 px-6 py-12 text-center text-sm text-destructive">
        {error}
      </div>
    );
  }

  if (!stages.length) {
    return (
      <>
        <EmptyState
          title="No stages yet"
          description="Add the first stage to build the format."
          action={
            <Button onClick={() => setEditor({ stage: null })}>
              <Plus className="h-4 w-4" />
              Add stage
            </Button>
          }
        />
        <StageEditorDialog
          open={!!editor}
          onOpenChange={(o) => !o && setEditor(null)}
          tournamentId={tournamentId}
          stages={stages}
          stage={editor?.stage || null}
          rosterCount={players.length}
        />
      </>
    );
  }

  // selectedId hasn't caught up with a fresh stage list yet (one render tick).
  if (!stage) {
    return (
      <div className="space-y-5">
        <div className="h-20 animate-pulse rounded-md bg-muted motion-reduce:animate-none" />
        <div className="h-64 animate-pulse rounded-md bg-muted motion-reduce:animate-none" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <StageStepper stages={stages} selectedId={selectedId} onSelect={setSelectedId} />
        <Button
          variant="outline"
          size="sm"
          disabled={!actions.canChangeStructure}
          title={
            !actions.canChangeStructure ? 'Stages lock once the first stage starts.' : undefined
          }
          onClick={() => setEditor({ stage: null })}
        >
          <Plus className="h-4 w-4" />
          Add stage
        </Button>
      </div>

      {!actions.canChangeStructure && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Lock className="h-3.5 w-3.5" />
          The stage order is locked because the competition has started.
        </div>
      )}

      <div className="rounded-md border border-border/60">
        <div className="flex flex-col gap-4 border-b border-border/60 p-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-3">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                Stage {index + 1} of {stages.length}
              </span>
              <StageStatusPill status={stage.status} />
            </div>
            <h3 className="mt-1 font-display text-xl font-semibold">{stage.name}</h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {prev && `Top ${stage.advanceCount} from ${prev.name} · `}
              {stageSummary(stage)}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {stage.status === 'pending' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  title="Rename the stage or change its settings"
                  onClick={() => setEditor({ stage })}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit stage
                </Button>
                {actions.canChangeStructure && prev && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Move stage earlier"
                    disabled={busy}
                    onClick={() =>
                      runWrite(
                        () => moveStage(tournamentId, stages, index, -1),
                        'Could not move the stage.',
                        'Stage moved.'
                      )
                    }
                  >
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                )}
                {actions.canChangeStructure && next && (
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label="Move stage later"
                    disabled={busy}
                    onClick={() =>
                      runWrite(
                        () => moveStage(tournamentId, stages, index, 1),
                        'Could not move the stage.',
                        'Stage moved.'
                      )
                    }
                  >
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                )}
                {actions.canDelete && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-destructive hover:text-destructive"
                    onClick={() => setConfirm('delete')}
                  >
                    Delete
                  </Button>
                )}
                <Button size="sm" disabled={!actions.canStart} onClick={() => setStartOpen(true)}>
                  <Play className="h-3.5 w-3.5" />
                  Start stage
                </Button>
              </>
            )}

            {stage.status === 'active' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  title="Rename the stage or change its settings"
                  onClick={() => setEditor({ stage })}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit stage
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  title="Delete every score in this stage and go back to Upcoming"
                  onClick={() => setConfirm('reset')}
                >
                  Reset scores
                </Button>
                <Button
                  size="sm"
                  title={
                    next
                      ? `Freeze the results and send the top players to ${next.name}`
                      : 'Freeze the results and crown the winner'
                  }
                  onClick={() => setCompleteOpen(true)}
                >
                  <Flag className="h-3.5 w-3.5" />
                  Complete stage
                </Button>
              </>
            )}

            {stage.status === 'completed' && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  title="Rename the stage or change its settings"
                  onClick={() => setEditor({ stage })}
                >
                  <Pencil className="h-3.5 w-3.5" />
                  Edit stage
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={!actions.canReopen}
                  title={
                    !actions.canReopen
                      ? `${next?.name} has started. Reset it first.`
                      : 'Unfreeze the results to change them'
                  }
                  onClick={() => setConfirm('reopen')}
                >
                  <RotateCcw className="h-3.5 w-3.5" />
                  Reopen
                </Button>
              </>
            )}
          </div>
        </div>

        {hint && (
          <div className="flex gap-3 border-b border-border/60 bg-heat/5 p-4">
            <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-heat" />
            <div className="min-w-0 text-sm">
              <p className="font-medium">{hint.title}</p>
              <p className="mt-0.5 text-muted-foreground">{hint.body}</p>
              {stage.status === 'pending' &&
                actions.startBlocked &&
                actions.startBlocked !== 'Already started.' && (
                  <p className="mt-1 text-xs text-warning">{actions.startBlocked}</p>
                )}
            </div>
          </div>
        )}

        {orphans.length > 0 && (
          <div className="flex flex-col gap-3 border-b border-border/60 bg-warning/5 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-3 text-sm">
              <UserX className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
              <p>
                {orphans.length === 1
                  ? '1 entry in this stage was deleted from the roster'
                  : `${orphans.length} entries in this stage were deleted from the roster`}{' '}
                and shows as “Removed player”.
                {!orphansRemovable && ' A started bracket can’t drop players; reset it first.'}
              </p>
            </div>
            {orphansRemovable && (
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                disabled={busy}
                onClick={() =>
                  runWrite(
                    () => removeStageParticipants(tournamentId, stage, orphans),
                    'Could not clean up the stage.',
                    'Removed from the stage.'
                  )
                }
              >
                Remove from stage
              </Button>
            )}
          </div>
        )}

        {hasVoteCategory(stage) && (
          <div className="border-b border-border/60 p-4">
            <div className="mb-3 flex items-center gap-3 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
              <span className="h-px w-6 bg-heat" />
              Community vote
            </div>
            <MappingVotingControl tournament={tournament} />
            <p className="mt-3 text-xs text-muted-foreground">
              {voterCount} {voterCount === 1 ? 'person has' : 'people have'} voted so far.
            </p>
          </div>
        )}

        <div className="p-4">
          {stage.status === 'pending' &&
            stage.type === 'leaderboard' &&
            ((stage.participants || []).length > 0 ? (
              <LeaderboardStageView stage={stage} playersById={playersById} />
            ) : (
              <div className="space-y-2">
                <LeaderboardStageView
                  stage={{ ...stage, participants: PLACEHOLDER_PARTICIPANTS, results: {} }}
                  playersById={PLACEHOLDER_PLAYERS_BY_ID}
                />
                <p className="text-xs text-muted-foreground">
                  {(stage.categories || []).map((c) => c.name).join(', ')}
                </p>
              </div>
            ))}

          {stage.status === 'pending' && stage.type === 'bracket' && (
            <BracketView
              stage={stage}
              playersById={playersById}
              placeholderCount={index === 0 ? players.length : stage.advanceCount}
              placeholderLabel={prev ? (n) => `${prev.name} #${n}` : undefined}
            />
          )}

          {stage.status === 'active' && stage.type === 'leaderboard' && (
            <LeaderboardResultsEditor
              tournamentId={tournamentId}
              stage={stage}
              index={index}
              players={players}
              playersById={playersById}
              votes={votes}
            />
          )}

          {stage.status === 'active' && stage.type === 'bracket' && (
            <div className="space-y-2">
              <BracketView stage={stage} playersById={playersById} onMatchClick={setMatchOpen} />
              <p className="text-xs text-muted-foreground">
                Click a match to enter or change its result.
              </p>
            </div>
          )}

          {stage.status === 'completed' && stage.type === 'leaderboard' && (
            <LeaderboardStageView
              stage={stage}
              playersById={playersById}
              nextStage={next}
              votes={votes}
            />
          )}

          {stage.status === 'completed' && stage.type === 'bracket' && (
            <BracketView stage={stage} playersById={playersById} />
          )}
        </div>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <details className="group rounded-md border border-border/60 p-4">
          <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-medium">
            <Copy className="h-4 w-4 text-muted-foreground" />
            Stream overlay link for {stage.name}
          </summary>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <Input
              value={overlayUrl}
              readOnly
              onFocus={(event) => event.target.select()}
              aria-label={`Stream overlay URL for ${stage.name}`}
              className="min-w-0 font-mono text-xs"
            />
            <Button type="button" variant="outline" onClick={copyOverlayUrl}>
              <Copy className="h-4 w-4" />
              Copy link
            </Button>
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            Add as a browser source in OBS. It updates live. Each stage has its own link.
          </p>
        </details>

        <details className="group rounded-md border border-border/60 p-4">
          <summary className="flex cursor-pointer list-none items-center gap-2 text-sm font-medium">
            <HelpCircle className="h-4 w-4 text-muted-foreground" />
            How stages work
          </summary>
          <ul className="mt-3 list-inside list-disc space-y-1.5 text-sm text-muted-foreground">
            <li>
              <span className="text-foreground">Upcoming:</span> set up the stage. Nothing is public
              yet beyond its name.
            </li>
            <li>
              <span className="text-foreground">Start stage:</span> choose who takes part. The stage
              goes Live.
            </li>
            <li>
              <span className="text-foreground">Live:</span> enter scores or match results. They
              save by themselves and show on the public page right away.
            </li>
            <li>
              <span className="text-foreground">Complete stage:</span> freezes the standings and
              sends the top players to the next stage. The last stage crowns the winner.
            </li>
            <li>
              <span className="text-foreground">Reopen</span> undoes a completion;{' '}
              <span className="text-foreground">Reset scores</span> wipes a live stage back to
              Upcoming.
            </li>
            <li>
              Adding, removing or reordering stages is only possible before the first stage starts.
            </li>
          </ul>
        </details>
      </div>

      <StageEditorDialog
        open={!!editor}
        onOpenChange={(o) => !o && setEditor(null)}
        tournamentId={tournamentId}
        stages={stages}
        stage={editor?.stage || null}
        rosterCount={players.length}
      />
      <StartStageDialog
        open={startOpen}
        onOpenChange={setStartOpen}
        tournamentId={tournamentId}
        stage={stage}
        index={index}
        players={players}
        playersById={playersById}
      />
      <CompleteStageDialog
        open={completeOpen}
        onOpenChange={setCompleteOpen}
        tournamentId={tournamentId}
        tournament={tournament}
        stages={stages}
        index={index}
        playersById={playersById}
        votes={votes}
      />
      {matchOpen && (
        <BracketResultDialog
          open
          onOpenChange={(o) => !o && setMatchOpen(null)}
          tournamentId={tournamentId}
          stage={stage}
          match={matchOpen}
          playersById={playersById}
        />
      )}

      <ConfirmDialog
        open={confirm === 'delete'}
        title={`Delete ${stage.name}?`}
        confirmLabel="Delete"
        destructive
        busy={busy}
        onOpenChange={(o) => !o && setConfirm(null)}
        onConfirm={() =>
          runWrite(
            () => deleteStage(tournamentId, stages, stage.id),
            'Could not delete the stage.',
            'Stage deleted.'
          )
        }
      />
      <ConfirmDialog
        open={confirm === 'reset'}
        title={`Reset ${stage.name}?`}
        description="Every result in this stage is deleted. Players stay seeded."
        confirmLabel="Reset"
        destructive
        busy={busy}
        onOpenChange={(o) => !o && setConfirm(null)}
        onConfirm={() =>
          runWrite(
            () => resetStage(tournamentId, stage.id),
            'Could not reset the stage.',
            `${stage.name} reset.`
          )
        }
      />
      <ConfirmDialog
        open={confirm === 'reopen'}
        title={`Reopen ${stage.name}?`}
        description={`Its standings unfreeze${next ? ` and ${next.name} loses its seeded players` : ''}.`}
        confirmLabel="Reopen"
        busy={busy}
        onOpenChange={(o) => !o && setConfirm(null)}
        onConfirm={() =>
          runWrite(
            () => reopenStage(tournamentId, stage.id, next?.id || null),
            'Could not reopen the stage.',
            `${stage.name} reopened.`
          )
        }
      />
    </div>
  );
};

export default StageManager;
